// ============================================================
// v8 — dispatch a CardDef's `builtin` behaviour to the engine.ts
// resolver it names (BOLT-007). This is the Pack path; a parity
// test asserts it is byte-identical to calling the resolver
// directly (pack.test.ts). Scripted cards (BOLT-009) get their own
// sandbox path — this module only handles `builtin`.
// ============================================================
import { ft } from './constants';
import {
  resolveBorders,
  resolveDirection,
  resolvePhotoPenalty,
  resolveQuadrants,
  resolveRadar,
  resolveSlice,
  type BordersResult,
  type DirectionResult,
  type QuadrantsResult,
  type RadarResult,
  type SliceResult,
} from './engine';
import type { CardDef } from './pack';
import type { Boundary, LngLat, Zone } from './protocol';

export interface BuiltinInputs {
  S: LngLat;
  H: LngLat;
  Z: Zone;
  Pstart?: LngLat; // thermometer walk start
  Pend?: LngLat; // thermometer walk end
  photoRadiusM?: number; // photo-penalty disk radius (settings.photoPenaltyRadiusFt → m)
  boundary?: Boundary; // v11 §2L: the Borders card cuts against the play boundary
}

const numParam = (params: Record<string, number | string> | undefined, key: string): number => {
  const v = params?.[key];
  if (typeof v !== 'number' || !Number.isFinite(v)) throw new Error(`builtin card missing numeric param "${key}"`);
  return v;
};

/** v24 BOLT-118: landmark cards are parameterised by an OSM TAG, not a number. */
export const tagParam = (params: Record<string, number | string> | undefined, key = 'tag'): string => {
  const v = params?.[key];
  if (typeof v !== 'string' || !v) throw new Error(`landmark card missing tag param "${key}"`);
  return v;
};

/** Resolve a built-in card to its new zone. Mirrors session.ts's direct calls. */
export function resolveBuiltin(def: CardDef, inp: BuiltinInputs): RadarResult | DirectionResult | SliceResult | QuadrantsResult | BordersResult | { zone: Zone } {
  const b = def.behavior;
  if (b.kind !== 'builtin') throw new Error(`resolveBuiltin: card "${def.id}" is not a builtin`);
  switch (b.ref) {
    case 'radar':
      return resolveRadar(inp.S, inp.H, inp.Z, ft(numParam(b.params, 'radiusFt')));
    case 'direction':
      return resolveDirection(inp.S, inp.H, inp.Z);
    case 'quadrants': // v11 §2K — axis-aligned cross through S
      return resolveQuadrants(inp.S, inp.H, inp.Z);
    case 'borders': // v11 §2L — outer band vs inner core of the boundary
      if (!inp.boundary) throw new Error('borders needs the play boundary');
      return resolveBorders(inp.H, inp.boundary, inp.Z);
    case 'thermometer': // wire ref stays 'thermometer'; the cut is now a Slice (v21 BOLT-101)
      if (!inp.Pstart || !inp.Pend) throw new Error('slice needs Pstart/Pend');
      return resolveSlice(inp.Pstart, inp.Pend, inp.H, inp.Z, ft(numParam(b.params, 'minFt')));
    case 'photoPenalty':
      if (inp.photoRadiusM === undefined) throw new Error('photoPenalty needs photoRadiusM');
      return resolvePhotoPenalty(inp.H, inp.Z, inp.photoRadiusM);
    case 'hider':
      throw new Error(`hider card "${def.id}" has no map effect`);
    case 'airhorn': // v16 BOLT-072: resolves as a delivery (index.ts castAirhorn), never geometry
      throw new Error(`airhorn card "${def.id}" has no map effect`);
    case 'nearest':
      // v24 BOLT-118: landmark cards need the harvested features + the cached
      // cells, which only the session holds, so they resolve in session.ts
      // (`applyNearestCard`) exactly like the airhorn resolves as a delivery.
      throw new Error(`landmark card "${def.id}" resolves from the session's cached cells`);
  }
}

/** Convenience: just the resulting zone (what the parity test compares). */
export function resolveBuiltinZone(def: CardDef, inp: BuiltinInputs): Zone {
  return resolveBuiltin(def, inp).zone;
}
